import { BaseComponent } from '../../components/base-component'
import { RentalIntentResponse, RentalPeriod } from '../../types/rental'
import { ConfiguratorProduct } from './configurator-product'

/**
 * Multi-product step component
 * Allows selection of one product from multiple options
 */
export class MultiProductStep extends BaseComponent {
  private products: ConfiguratorProduct[] = []
  private nextButton: HTMLButtonElement | null = null
  private selectedProducts: Map<string, number> = new Map() // productId -> variantId
  private hasCreatedRentalIntent: boolean = false
  private rentalIntentTokens: Map<string, string> = new Map()
  private allowMultiple: boolean = false
  private requireAllProducts: boolean = false

  connectedCallback() {
    super.connectedCallback()
    this.allowMultiple = this.dataset.allowMultiple === 'true'
    this.requireAllProducts = this.dataset.requireAllProducts === 'true'

    this.initializeElements()

    // Set initial button state
    if (this.nextButton) {
        if (this.dataset.requireAllProducts === 'false') {
            // Optional step - enable button by default
            this.nextButton.disabled = false
            this.nextButton.classList.add('product-configurator__step-footer-button--active')
        } else {
            // Mandatory step - disable button by default
            this.nextButton.disabled = true
            this.nextButton.classList.remove('product-configurator__step-footer-button--active')
        }
    }
  }

  /**
   * Initializes DOM elements and product instances
   */
  private initializeElements() {
    this.nextButton = this.querySelector('.product-configurator__step-footer-button')

    // Add click handler for next button
    this.nextButton?.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation() // Prevent event bubbling

        console.log('Next button clicked:', {
            isDisabled: this.nextButton?.disabled,
            isOptional: this.dataset.requireAllProducts === 'false',
            selectedProductsCount: this.selectedProducts.size
        })

        if (!this.nextButton?.disabled) {
            // For optional steps with no selection
            if (this.dataset.requireAllProducts === 'false' && this.selectedProducts.size === 0) {
                console.log('Handling optional step with no selections')
                this.dispatchEvent(new CustomEvent('step:complete', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        skipStep: true,
                        selectedProducts: new Map(),
                        stepIndex: this.getIndex() // Add step index
                    }
                }))
            } else {
                // For steps with selections
                console.log('Handling step with selections')
                this.dispatchEvent(new CustomEvent('step:complete', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        skipStep: false,
                        selectedProducts: this.selectedProducts,
                        stepIndex: this.getIndex() // Add step index
                    }
                }))
            }
        }
    })

    const productElements = this.querySelectorAll('.product-configurator__step-product')
    productElements.forEach(element => {
      this.products.push(new ConfiguratorProduct(
        element as HTMLElement,
        this.handleProductSelected.bind(this)
      ))
    })

    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.product-configurator__select')) {
        const activeSelects = this.querySelectorAll('.product-configurator__select--active')
        activeSelects.forEach(select => {
          select.classList.remove('product-configurator__select--active')
        })
      }
    })

    this.initializeListeners()
  }

  private initializeListeners() {
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.product-configurator__select')) {
        const activeSelects = this.querySelectorAll('.product-configurator__select--active')
        activeSelects.forEach(select => {
          select.classList.remove('product-configurator__select--active')
        })
      }
    })
  }

  /**
   * Handles product selection
   * @param productId - ID of the selected product
   * @param variantId - ID of the selected variant
   */
  private handleProductSelected(productId: string, variantId: number) {
    console.log('handleProductSelected called with:', { productId, variantId })

    if (!this.allowMultiple) {
        console.log('Single selection mode - clearing other products')
        this.selectedProducts.clear()
        this.products.forEach(product => {
            if (product.productId !== productId) {
                console.log('Clearing selection for product:', product.productId)
                product.clearSelection()
            }
        })
    }

    this.selectedProducts.set(productId, variantId)
    this.updateNextButton()

    // Dispatch change event when selection is updated
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /**
   * Handles product deselection
   * @param productId - ID of the deselected product
   */
  private handleProductDeselected(productId: string) {
    console.log('Deselecting product:', productId)
    const button = this.querySelector(`.product-configurator__step-product[data-product-id="${productId}"] .product-configurator__step-product-button`)
    if (button) {
      button.classList.remove('product-configurator__step-product-button--selected')
    }

    this.selectedProducts.delete(productId)

    // Re-enable all products and check their variant state
    this.products.forEach(product => {
      product.enable()
      const selectButton = this.querySelector(`.product-configurator__step-product[data-product-id="${product.productId}"] .product-configurator__step-product-button`)

      if (selectButton) {
        // Only disable if no variant is selected
        if (!product.currentVariantId) {
          selectButton.setAttribute('disabled', '')
        } else {
          selectButton.removeAttribute('disabled')
        }
      }
    })

    if (this.nextButton) {
      this.nextButton.disabled = true
      this.nextButton.classList.remove('product-configurator__step-footer-button--active')
    }

    this.updateNextButton()
  }

  /** Activates the step */
  activate() {
    this.classList.add('product-configurator__step--active')
  }

  /** Deactivates the step */
  deactivate() {
    this.classList.remove('product-configurator__step--active')
  }

  /** Marks the step as completed */
  complete() {
    this.classList.add('product-configurator__step--completed')
  }

  /**
   * Gets the step index
   * @returns The step index or -1 if not set
   */
  getIndex(): number {
    return parseInt(this.dataset.index || '-1')
  }

  /**
   * Checks if the step is valid
   * @returns True if a variant is selected
   */
  isValid(): boolean {
    // If allowMultiple is false, this is an optional step that can be skipped
    if (!this.allowMultiple) {
      return true
    } else if (this.requireAllProducts) {
      // If requireAllProducts is true, all products must have variants selected
      return this.selectedProducts.size === this.products.length
    } else {
      // Default case: at least one product must have a variant selected
      return this.selectedProducts.size > 0
    }
  }

  /**
   * Gets the currently selected variant IDs
   * @returns Selected variant IDs
   */
  getCurrentVariantIds(): Map<string, number> {
    return this.selectedProducts
  }

  /**
   * Checks if a rental intent has been created
   * @returns True if rental intent exists
   */
  hasRentalIntent(productId?: string): boolean {
    if (!this.allowMultiple) return this.hasCreatedRentalIntent
    return productId ? this.rentalIntentTokens.has(productId) : false
  }

  /**
   * Gets a rental period by duration for the selected product
   * @param duration - Duration in days
   * @returns Matching rental period or null if not found
   */
  getRentalPeriodByDuration(duration: number, productId?: string): RentalPeriod | null {
    const product = this.products.find(p => p.productId === productId);
    return product?.getRentalPeriodByDuration(duration) || null;
  }

  /**
   * Sets the rental intent token
   * @param response - Rental intent response from API
   */
  setRentalIntentToken(response: RentalIntentResponse, productId: string) {
    // Store the stringified response
    this.rentalIntentTokens.set(productId, JSON.stringify(response))
    if (!this.allowMultiple) {
      this.hasCreatedRentalIntent = true
    }
  }

  /**
   * Updates available variants for a specific product
   * @param productId - ID of the product to update
   * @param availableVariantIds - Array of available variant IDs
   */
  updateAvailableVariants(productId: string | number, availableVariantIds: number[]) {
    const product = this.products.find(p => p.productId === String(productId))
    product?.updateAvailableVariants(availableVariantIds)
  }

  getCurrentVariantId(): number | null {
    if (this.allowMultiple) return null;
    const [firstVariantId] = [...this.selectedProducts.values()];
    return firstVariantId || null;
  }

  getRentalIntentToken(productId: string): string {
    return this.rentalIntentTokens.get(productId) || ''
  }

  getAllRentalIntentTokens(): Map<string, string> {
    return new Map(this.rentalIntentTokens)
  }

  getProductDetails() {
    const details: Array<{
      productId: string,
      title: string,
      options: Record<string, string>
    }> = []

    this.selectedProducts.forEach((variantId, productId) => {
      const product = this.products.find(p => p.productId === productId)
      if (product) {
        details.push({
          productId,
          title: product.element.dataset.productTitle || '',
          options: product.getSelectedOptions() || {}
        })
      }
    })

    return details
  }

  reset() {
    this.selectedProducts.clear()
    this.hasCreatedRentalIntent = false
    this.rentalIntentTokens.clear()
    this.classList.remove('product-configurator__step--completed')

    // Reset all products
    this.products.forEach(product => product.reset())

    // Reset next button based on whether the step is optional
    const nextButton = this.querySelector('.product-configurator__step-footer-button') as HTMLButtonElement
    if (nextButton) {
        if (this.dataset.requireAllProducts === 'false') {
            // Optional step - enable button by default
            nextButton.disabled = false
            nextButton.classList.add('product-configurator__step-footer-button--active')
        } else {
            // Mandatory step - disable button by default
            nextButton.disabled = true
            nextButton.classList.remove('product-configurator__step-footer-button--active')
        }
    }
  }

  private updateNextButton() {
    if (!this.nextButton) return

    console.log('Updating next button:', {
        isOptional: this.dataset.requireAllProducts === 'false',
        allowMultiple: this.allowMultiple,
        requireAllProducts: this.requireAllProducts,
        selectedProductsCount: this.selectedProducts.size,
        totalProducts: this.products.length
    })

    // Optional steps always have enabled button
    if (this.dataset.requireAllProducts === 'false') {
        console.log('Optional step - keeping next button enabled')
        this.nextButton.disabled = false
        this.nextButton.classList.add('product-configurator__step-footer-button--active')
        return
    }

    // For mandatory steps, check selection requirements
    let isValid = false
    if (!this.allowMultiple) {
        // Single selection mode - need exactly one selection
        isValid = this.selectedProducts.size === 1
    } else if (this.requireAllProducts) {
        // All products must be selected
        isValid = this.selectedProducts.size === this.products.length
    } else {
        // At least one product must be selected
        isValid = this.selectedProducts.size > 0
    }

    console.log('Next button validity:', isValid)
    this.nextButton.disabled = !isValid

    if (isValid) {
        this.nextButton.classList.add('product-configurator__step-footer-button--active')
    } else {
        this.nextButton.classList.remove('product-configurator__step-footer-button--active')
    }
  }

  clearRentalIntentTokens() {
    this.rentalIntentTokens.clear()
    this.hasCreatedRentalIntent = false
  }
}
